import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import Icon from '~ComponentsImproved/Icon';
import noteIcon from '../../../../../Assets/icons/table-note.svg';
import CodeInput from '../../../Components/CodeInput';
import Modal, { MODAL_TRANSITION_MS } from '../../../Components/Modal';

/** Longest comment the shopper can leave. */
export const COMMENT_MAX_LENGTH = 100;

/** Same entrance/exit pattern as PromoCodeModal: keyframe in, CSSTransition-driven out. */
/** The card: scales and fades in, and back out. */
const Card = styled.div`
  position: relative;
  width: min(100%, 860px);
  padding: 48px;
  border-radius: 36px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background: var(--eposSurface);
  color: var(--eposText);
  box-shadow: 0 40px 100px rgba(0, 0, 0, 0.45);
  transform: translate3d(0, 0, 0) scale(1);
  opacity: 1;
  animation: eposCommentCardIn ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${MODAL_TRANSITION_MS}ms cubic-bezier(0.22, 1, 0.36, 1),
    opacity ${MODAL_TRANSITION_MS}ms ease-out;
  will-change: transform, opacity;

  .epos-comment-exit-active &,
  .epos-comment-exit-done & {
    animation: none;
    transform: translate3d(0, 24px, 0) scale(0.96);
    opacity: 0;
  }

  @keyframes eposCommentCardIn {
    from {
      transform: translate3d(0, 24px, 0) scale(0.96);
      opacity: 0;
    }
    to {
      transform: translate3d(0, 0, 0) scale(1);
      opacity: 1;
    }
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -0.6px;
`;

interface Props {
  open: boolean;
  /** The comment so far, shown in the field so it can be changed. */
  defaultValue: string;
  /** Called with the trimmed text, empty to clear. */
  onSave: (comment: string) => void;
  onClose: () => void;
}

/**
 * Free-text comment entry, capped at COMMENT_MAX_LENGTH. Same card as the
 * promo code modal; the field is CodeInput in free-text mode with the EPOS
 * keyboard folded out under it, so typing stays in the DOM and nothing
 * renders until "Save".
 */
const CommentModal: FC<Props> = ({ open, defaultValue, onSave, onClose }) => {
  const { i18n } = useI18n();

  return (
    <Modal
      open={open}
      exitPrefix="epos-comment"
      onBackdropTap={onClose}
      testId="epos-comment-modal">
      <Card>
        <Header>
          <Icon src={noteIcon} color="var(--eposText)" width="36px" height="36px" />
          <Title>{i18n('Express.Comment.Title')}</Title>
        </Header>
        <CodeInput
          freeText
          maxLength={COMMENT_MAX_LENGTH}
          defaultValue={defaultValue}
          placeholder={i18n('Express.Comment.Placeholder')}
          submitLabel={i18n('App.Save')}
          onSubmit={onSave}
        />
      </Card>
    </Modal>
  );
};

export default memo(CommentModal);
